import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { HoneycombBackground } from '../components/Auth/HoneycombBackground';
import { LanguageSelector } from '../components/LanguageSelector';
import { FaTimes } from 'react-icons/fa';

const BRIX_LOGO_URL = 'https://firebasestorage.googleapis.com/v0/b/brix-v2.firebasestorage.app/o/Marca%2FRecurso%205.svg?alt=media&token=65d56a39-541f-4ec0-864f-fc2105cf9c54';

const carouselFeatures = [
  'welcome.carousel.feature1',
  'welcome.carousel.feature2',
  'welcome.carousel.feature3',
  'welcome.carousel.feature4',
];

const WelcomeScreen: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [showModal, setShowModal] = useState(false);
  const [showSectorModal, setShowSectorModal] = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(0);

  useEffect(() => {
    const interval = setInterval(() => {
      setCarouselIndex((prev) => (prev + 1) % carouselFeatures.length);
    }, 3500);
    return () => clearInterval(interval);
  }, []);

  return (
    <div className="w-screen min-h-screen flex flex-row bg-gradient-to-br from-[#f8e8e8] via-[#fff] to-[#ffe3e3] relative overflow-x-hidden overflow-y-auto z-10 font-['Montserrat']">
      <HoneycombBackground />
      {/* Columna izquierda con efecto decorativo y carrusel */}
      <div className="flex-1 hidden md:flex items-center justify-center relative overflow-hidden">
        {/* <div className="w-3/4 h-3/4 rounded-[60px] bg-gradient-to-br from-[#D50B3F]/70 via-[#fff]/60 to-[#B3122C]/60 shadow-2xl blur-[2px] animate-float absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2" /> */}
        <div className="absolute top-1/4 left-1/4 w-32 h-32 bg-gradient-to-tr from-[#B3122C]/60 to-[#D50B3F]/40 rounded-full blur-2xl opacity-70 animate-pulse-slow" />
        {/* Carrusel de funciones */}
        <div className="absolute inset-0 flex flex-col items-center justify-center z-10 pointer-events-none select-none">
          <div className="w-[80%] max-w-lg min-h-[120px] flex flex-col items-center justify-center">
            {carouselFeatures.map((feature, idx) => (
              <div
                key={feature}
                className={`transition-all duration-700 ease-in-out absolute w-full text-center ${idx === carouselIndex ? 'opacity-100 scale-100 translate-y-0 z-20' : 'opacity-0 scale-90 translate-y-8 z-0'} pointer-events-none`}
                aria-hidden={idx !== carouselIndex}
              >
                <div className="inline-block px-8 py-6 bg-white/70 backdrop-blur-xl border border-white/30 rounded-2xl shadow-xl text-2xl font-bold text-[#B3122C] tracking-tight animate-fadeInUp">
                  {t(feature)}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      {/* Columna derecha con contenido */}
      <div className="flex-1 min-h-screen flex flex-col justify-center items-center relative z-10">
        {/* Selector de idioma en la esquina superior derecha */}
        <div className="absolute top-6 right-8 z-20">
          <LanguageSelector />
        </div>
        <div className="bg-white/70 backdrop-blur-2xl border border-white/30 rounded-3xl shadow-2xl w-full max-w-lg relative z-10 flex flex-col items-center px-12 py-16 animate-fadeInUp">
          <h1 className="text-5xl font-extrabold text-transparent bg-clip-text bg-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] mb-6 text-center drop-shadow-lg tracking-tight animate-gradient-x" aria-label="welcome.title">
            {t('welcome.title', { defaultValue: 'Meet your NEW & IMPROVED project platform' })}
          </h1>
          <p className="text-gray-700 text-xl mb-10 text-center max-w-md font-medium animate-fadeIn delay-200" aria-label="welcome.subtitle">
            {t('welcome.subtitle', { defaultValue: 'Connect talent and opportunities in one place.' })}
          </p>
          <div className="flex gap-6 w-full justify-center animate-fadeIn delay-300">
            <button
              className="bg-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] hover:from-[#D50B3F] hover:to-[#B3122C] text-white px-10 py-4 rounded-2xl font-bold text-lg shadow-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#D50B3F]/30 animate-bounceIn"
              onClick={() => navigate('/auth/login')}
              aria-label="welcome.login"
            >
              {t('welcome.login', { defaultValue: 'Log In' })}
            </button>
            <button
              className="bg-white/80 border-2 border-[#D50B3F] text-[#B3122C] hover:bg-[#fff0f0] px-10 py-4 rounded-2xl font-bold text-lg shadow-xl transition-all duration-300 transform hover:scale-105 focus:outline-none focus:ring-4 focus:ring-[#D50B3F]/20 animate-bounceIn delay-100"
              onClick={() => setShowModal(true)}
              aria-label="welcome.register"
            >
              {t('welcome.register', { defaultValue: 'Register' })}
            </button>
          </div>
        </div>
        {/* Logo en la esquina inferior derecha */}
        <img
          src={BRIX_LOGO_URL}
          alt="Brix Logo"
          className="fixed bottom-6 right-8 w-32 h-auto opacity-95 z-30 drop-shadow-2xl select-none pointer-events-none animate-fadeIn"
          style={{ minWidth: 80 }}
        />
      </div>
      {/* Modal de selección de tipo de registro */}
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-fadeIn">
          <div className="bg-white/90 backdrop-blur-2xl rounded-2xl shadow-2xl p-10 max-w-md w-full relative flex flex-col items-center border border-white/30 animate-fadeInUp">
            <button className="absolute top-3 right-4 text-2xl text-gray-400 hover:text-red-600" onClick={() => setShowModal(false)} aria-label={t('welcome.close', { defaultValue: 'Cerrar' })}><FaTimes /></button>
            <h2 className="text-2xl font-bold text-red-600 mb-6 text-center animate-fadeIn" aria-label="welcome.chooseType">{t('welcome.chooseType', { defaultValue: 'How do you want to register?' })}</h2>
            <div className="flex flex-col gap-4 w-full">
              <button
                className="w-full px-6 py-4 bg-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] hover:from-[#D50B3F] hover:to-[#B3122C] text-white rounded-xl font-bold text-lg transition-all duration-200 shadow-lg animate-bounceIn hover:scale-105 hover:shadow-xl"
                onClick={() => { setShowModal(false); navigate('/auth/register'); }}
                aria-label="welcome.asWorker"
              >
                {t('welcome.asWorker', { defaultValue: 'As Worker' })}
              </button>
              <button
                className="w-full px-6 py-4 bg-white/80 border-2 border-[#D50B3F] text-[#B3122C] hover:bg-[#fff0f0] rounded-xl font-bold text-lg transition-all duration-200 shadow-lg animate-bounceIn delay-100 hover:scale-105 hover:shadow-xl"
                onClick={() => { setShowModal(false); navigate('/auth/employee-register'); }}
                aria-label="welcome.asEmployee"
              >
                {t('welcome.asEmployee', { defaultValue: 'As Employee' })}
              </button>
              <button
                className="w-full px-6 py-4 bg-white/80 border-2 border-[#D50B3F] text-[#B3122C] hover:bg-[#fff0f0] rounded-xl font-bold text-lg transition-all duration-200 shadow-lg animate-bounceIn delay-200 hover:scale-105 hover:shadow-xl"
                onClick={() => { setShowModal(false); setShowSectorModal(true); }}
                aria-label="welcome.asBusiness"
              >
                {t('welcome.asBusiness', { defaultValue: 'As Business' })}
              </button>
            </div>
          </div>
        </div>
      )}
      {showSectorModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-40 animate-fadeIn">
          <div className="bg-white/90 backdrop-blur-2xl rounded-2xl shadow-2xl p-10 max-w-md w-full relative flex flex-col items-center border border-white/30 animate-fadeInUp">
            <button className="absolute top-3 right-4 text-2xl text-gray-400 hover:text-red-600" onClick={() => setShowSectorModal(false)} aria-label={t('welcome.close', { defaultValue: 'Cerrar' })}><FaTimes /></button>
            <h2 className="text-2xl font-bold text-red-600 mb-6 text-center animate-fadeIn" aria-label="welcome.chooseSector">{t('welcome.chooseSector', { defaultValue: '¿En qué sector se encuentra tu empresa?' })}</h2>
            <div className="flex flex-col gap-4 w-full">
              <button
                className="w-full px-6 py-4 bg-white text-transparent bg-clip-text bg-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] border-2 border-transparent border-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] rounded-xl font-bold text-lg transition-all duration-200 shadow-lg animate-bounceIn"
                style={{ borderImage: 'linear-gradient(to right, #B3122C, #D50B3F, #B3122C) 1' }}
                onClick={() => { setShowSectorModal(false); navigate('/auth/health-business-register'); }}
                aria-label="welcome.sectorHealth"
              >
                <span className="bg-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] bg-clip-text text-transparent">{t('welcome.sectorHealth', { defaultValue: 'Sector Salud' })}</span>
              </button>
              <button
                className="w-full px-6 py-4 bg-gradient-to-r from-[#B3122C] via-[#D50B3F] to-[#B3122C] text-white rounded-xl font-bold text-lg transition-all duration-200 shadow-lg animate-bounceIn delay-100"
                onClick={() => { setShowSectorModal(false); navigate('/auth/business-register'); }}
                aria-label="welcome.sectorConstruction"
              >
                {t('welcome.sectorConstruction', { defaultValue: 'Sector Construcción' })}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Animaciones keyframes y utilidades posmodernas */}
      <style>{`
        @keyframes gradient-x {
          0%, 100% { background-position: 0% 50%; }
          50% { background-position: 100% 50%; }
        }
        .animate-gradient-x {
          background-size: 200% 200%;
          animation: gradient-x 6s ease-in-out infinite;
        }
        @keyframes fadeInUp {
          0% { opacity: 0; transform: translateY(40px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .animate-fadeInUp {
          animation: fadeInUp 0.8s cubic-bezier(.39,.575,.565,1.000) both;
        }
        @keyframes bounceIn {
          0% { opacity: 0; transform: scale(0.8); }
          60% { opacity: 1; transform: scale(1.05); }
          100% { opacity: 1; transform: scale(1); }
        }
        .animate-bounceIn {
          animation: bounceIn 0.7s cubic-bezier(.39,.575,.565,1.000) both;
        }
        .animate-bounceIn.delay-100 { animation-delay: 0.1s; }
        .animate-fadeIn.delay-200 { animation-delay: 0.2s; }
        .animate-fadeIn.delay-300 { animation-delay: 0.3s; }
        @keyframes pulse-slow {
          0%, 100% { opacity: 0.7; }
          50% { opacity: 1; }
        }
        .animate-pulse-slow {
          animation: pulse-slow 3s infinite;
        }
        @keyframes float {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        .animate-float {
          animation: float 5s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default WelcomeScreen; 